<template>
  <DashboardSlideOut
    class="explorer-filters-slide-out"
    label="Report Selections"
    :show="show"
    :disabled="hasError"
    @cancel="$emit('cancel')"
    @complete="$emit('complete', localValue)"
  >
    <template v-slot:slideOutContent>
      <Tabs
        id="tabs"
        :tabs="tabs"
        :currentTab="currentTab"
        @click="handleTabClick"
      ></Tabs>
      <MessageBarV02
        v-if="showNewExplorerMessages"
        class="message"
        color="#223448"
        dark
        dense
        tile
      >
        {{ slideOutBanner }}
      </MessageBarV02>
      <SearchInput
        v-model="searchTerm"
        class="search"
        name="search"
        :placeholder="searchPlaceholder"
      ></SearchInput>
      <DimensionsTab
        v-if="isDimensionsTab"
        :value="localDimensions"
        :dimensions="filteredDimensions"
        @input="updateReportField('dimensions', $event)"
      ></DimensionsTab>
      <MetricsTab
        v-else-if="isMetricsTab"
        :value="localMetrics"
        :metrics="filteredMetrics"
        @input="updateReportField('metrics', $event)"
      ></MetricsTab>
      <FiltersTab
        v-else-if="isFiltersTab"
        :value="localFilters"
        :dimensions="filteredDimensions"
        :portfolioBrands="portfolioBrands"
        :useSalesInsightsIfAvailable="isSalesInsights"
        @input="updateReportField('filters', $event)"
      ></FiltersTab>
      <div
        v-if="!hasSearchResults"
        class="no-filters-results"
      >
        No {{ currentTab }}
      </div>
    </template>
  </DashboardSlideOut>
</template>
<script>
import { mapState } from 'vuex';
import { MessageBarV02, SearchInput, Tabs } from '@mikmak/components';
import {
  getAvailableDimensions,
  getAvailableGroups,
  getAvailableMetrics,
} from '../../../../services/dashboard/exclusionsService';
import {
  getReportFieldsFromValues,
} from '../../../../services/dashboard/reportFieldService';
import DashboardSlideOut from '../../Common/DashboardSlideOut.vue';
import DimensionsTab from './DimensionsTab.vue';
import FiltersTab from './FiltersTab.vue';
import MetricsTab from './MetricsTab.vue';

export default {
  props: {
    value: Object,
    show: Boolean,
    dimensions: Array,
    metrics: Array,
    portfolioBrands: Array,
  },
  components: {
    DashboardSlideOut,
    DimensionsTab,
    FiltersTab,
    MessageBarV02,
    MetricsTab,
    SearchInput,
    Tabs,
  },
  data() {
    return {
      localValue: this.value,
      tabs: ['Dimensions', 'Metrics', 'Filters'],
      currentTab: 'Dimensions',
      searchTerm: '',
    };
  },
  computed: {
    ...mapState({
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
      showNewExplorerMessages: state => state.main.showNewExplorerMessages,
    }),
    isDimensionsTab() {
      return this.currentTab === 'Dimensions';
    },
    isMetricsTab() {
      return this.currentTab === 'Metrics';
    },
    isFiltersTab() {
      return this.currentTab === 'Filters';
    },
    searchPlaceholder() {
      return `Search ${this.currentTab}`;
    },
    lowerSearchTerm() {
      return this.searchTerm.toLowerCase();
    },
    hasError() {
      return !this.localDimensions
        || !this.localDimensions.length
        || !this.localMetrics
        || !this.localMetrics.length;
    },
    isSalesInsights() {
      const availableGroups = getAvailableGroups(this.allLocalReportFields);

      return availableGroups.length === 1 && availableGroups[0] === 'sales';
    },
    allLocalReportFields() {
      return this.localDimensions.concat(this.localFilterDimensions).concat(this.localMetrics);
    },
    localDimensions() {
      return this.localValue.dimensions;
    },
    localMetrics() {
      return this.localValue.metrics;
    },
    localFilters() {
      return this.localValue.filters;
    },
    localFilterDimensions() {
      return getReportFieldsFromValues(this.allReportFields, this.localFilters.map(f => f.key));
    },
    availableDimensions() {
      return getAvailableDimensions(
        this.dimensions,
        this.localDimensions,
        this.localFilterDimensions,
        this.localMetrics,
      );
    },
    availableMetrics() {
      return getAvailableMetrics(
        this.metrics,
        this.localDimensions,
        this.localFilterDimensions,
        this.localMetrics,
      );
    },
    hasSearchResults() {
      return this.isMetricsTab
        ? this.filteredMetrics && this.filteredMetrics.length
        : this.filteredDimensions && this.filteredDimensions.length;
    },
    filteredDimensions() {
      return this.availableDimensions.filter(d =>
        this.containsSearchTerm(d.key) || this.containsSearchTerm(d.category),
      );
    },
    filteredMetrics() {
      return this.availableMetrics.filter(m => this.containsSearchTerm(m.key));
    },
    isDisabled() {
      return !this.brandIds || !this.brandIds.length;
    },
    brandIds() {
      return this.portfolioBrands.reduce((brandIds, { checked, value }) => {
        if (checked) {
          brandIds.push(value);
        }

        return brandIds;
      }, []);
    },
    defaultBrandsFilterValue() {
      return this.portfolioBrands.reduce((brands, { checked, key, value }) => {
        if (checked) {
          brands.push({ key, value });
        }

        return brands;
      }, []);
    },
    filterableDimensions() {
      return this.dimensions.filter(d => d.canFilter);
    },
    slideOutBanner() {
      const parameterType = this.isFiltersTab ? 'Brands' : this.currentTab;

      return `For an optimal experience, please select up to 5 ${parameterType}.`;
    },
  },
  methods: {
    updateReportField(name, val) {
      this.localValue[name] = val;

      this.$emit('input', this.localValue);
      this.$emit('change', this.localValue);
    },
    handleTabClick(tab) {
      this.currentTab = tab;
    },
    goToTab(tab) {
      const eventTab = this.getTabFromEvent(tab);

      if (eventTab) {
        this.currentTab = eventTab;
      }
    },
    getTabFromEvent(tab) {
      if (!tab) {
        return '';
      }

      switch (tab.toLowerCase()) {
        case 'dimensions':
          return 'Dimensions';
        case 'metrics':
          return 'Metrics';
        case 'filters':
          return 'Filters';
        default:
          return '';
      }
    },
    containsSearchTerm(str) {
      return str.toLowerCase().indexOf(this.lowerSearchTerm) >= 0;
    },
  },
  watch: {
    value() {
      this.localValue = this.value;
    },
  },
};
</script>
<style scoped>
#tabs {
  border: 1.5px solid #C1CCD4;
  border-radius: 4px;
  margin-bottom: 24px;
  height: 44px;
}

.search {
  margin-bottom: 24px;
}
.message {
  margin-top: -16px;
  margin-bottom: 24px;
}

::v-deep .vuetify-styling .v-alert__wrapper {
  padding: 0 !important;
}

</style>
<style>
.explorer-filters-slide-out #tabs {
  display: flex;
  justify-content: space-evenly;
}

.explorer-filters-slide-out #tabs .tab {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  width: 117px;
  border-radius: 4px;
  margin: 5px;
  padding-bottom: 0;
}

.explorer-filters-slide-out #tabs .tab.active {
  color: #FFFFFF;
  background-color: #4369F4;
}

.explorer-filters-slide-out #tabs .current-tab-indicator {
  display: none;
}
</style>
